import MarkdownIt from 'markdown-it'
import milr from 'markdown-it-replace-link'
import mila from 'markdown-it-link-attributes'
import { ObserveVisibility } from 'vue-observe-visibility'

export const useAnalyticsJS = () => {
  const { locale } = useI18n()

  return {
    page(name) {
      const { cookiesEnabledIds, isConsentGiven } = useCookieControl()
      window.analytics.page(name, {
        'Ads Storage Consent State': isConsentGiven.value ? cookiesEnabledIds.value?.includes('advertising') : false,
        'Analytics Storage Consent State': isConsentGiven.value ? cookiesEnabledIds.value?.includes('analytics') : false,
        'Ad User Data Consent State': isConsentGiven.value ? cookiesEnabledIds.value?.includes('advertising') : false,
        'Ad Personalization Consent State': isConsentGiven.value ? cookiesEnabledIds.value?.includes('advertising') : false,
        'locale': locale.value,
        name,
        'path': window.location.pathname,
        'title': name,
        'url': window.location.href,
      })
    },
    async track(name, attributes) {
      window.analytics.track(name, {
        ...attributes,
        label: attributes.label || name,
        locale: locale.value,
        page: window.location.pathname,
        value: 1,
      })

      return await new Promise((resolve) => {
        window.setTimeout(() => {
          resolve()
        }, 300)
      })
    },
  }
}

export const useAvailableLanguages = () => {
  const allLanguages = ref([
    { name: 'English', slug: 'en' },
    { name: 'French', slug: 'fr' },
    { name: 'German', slug: 'de' },
    { name: 'Spanish', slug: 'es' },
  ])

  const languages = useState('available-languages', () => [
    { name: 'English', slug: 'en' },
    { name: 'French', slug: 'fr' },
    { name: 'German', slug: 'de' },
    { name: 'Spanish', slug: 'es' },
  ])

  function setAvailableLanguages(availableLanguages = ['de', 'en', 'es', 'fr']) {
    languages.value = allLanguages.value.filter(({ slug }) => availableLanguages.includes(slug))
  }

  return {
    languages,
    setAvailableLanguages,
  }
}

export const useBreadcrumb = () => {
  const listItems = useState('agency-form-is-visible', () => [])

  function updateListItems(list) {
    listItems.value = list
  }

  return {
    listItems,
    updateListItems,
  }
}

export const useCamelCase = () => {
  function camelCase(str) {
    return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
  }

  return {
    camelCase,
  }
}

export const useChilipiper = () => {
  // Composables
  const { track } = useAnalyticsJS()
  const { locale } = useI18n()
  const { $chilipiper } = useNuxtApp()

  // Refs
  const lead = ref({})

  // Computed Properties
  const demoFormId = computed(() => {
    switch (locale.value) {
      case 'de':
        return 'cb887e10-e2ce-48d0-b961-15e1d493d0a6'
      case 'es':
        return '860d36ce-8c02-4b68-b0d2-4b03632d8f7d'
      case 'fr':
        return 'b1f081cb-6de5-424f-b2f9-5b7f56e237d3'
      default:
        return '5c4f470d-280b-4686-998d-4b435bce1025'
    }
  })

  const transformedLocale = computed(() => {
    switch (locale.value) {
      case 'fr':
        return 'fr_FR'
      default:
        return 'en_US'
    }
  })

  // Methods
  async function onMessage(event) {
    if (
      event.data.id === demoFormId.value
      && event.data.type === 'hsFormCallback'
      && event.data.eventName === 'onFormSubmitted'
    ) {
      lead.value = event.data.data.submissionValues
      $chilipiper.submit({
        lead: lead.value,
        locale: transformedLocale.value,
      })
      nextTick(() => {
        document.body.classList.add('no-scroll')
      })
    }

    if (
      Object.keys(lead.value).length > 0
      && event.data.action === 'booking-confirmed'
    ) {
      track('Demo Scheduled', {
        label: 'Demo Scheduled',
        category: 'Demo',
        booking_flow: 'routed',
        ...lead.value,
      })

      if (window.dataLayer) {
        const { v4: uuidv4 } = await import('uuid')
        dataLayer.push({ event: `event.demoScheduled.${uuidv4()}` })
      }
    }

    if (
      Object.keys(lead.value).length > 0
      && (event.data.action === 'close'
        || event.data.action === 'no-action-choice')
    ) {
      document.body.classList.remove('no-scroll')
    }
  }

  // Lifecycle hooks
  onBeforeUnmount(() => {
    window.removeEventListener('message', onMessage)
  })

  onMounted(() => {
    window.addEventListener('message', onMessage)
  })
}

export const useColorScheme = () => {
  const theme = useState('color-scheme', () => 'light')

  function useDarkMode() {
    theme.value = 'dark'
  }

  function useLightMode() {
    theme.value = 'light'
  }

  return {
    theme,
    useDarkMode,
    useLightMode,
  }
}

export const useConditionalPreviewMode = () => {
  const { public: publicRuntimeConfig } = useRuntimeConfig()
  return usePreviewMode({
    getState: () => {
      return {
        preview: true,
      }
    },
    shouldEnable: () => {
      return publicRuntimeConfig.buildType !== 'production'
    },
  })
}

export const useCountryList = () => {
  const countryList = ref({
    'Afghanistan': 'AF',
    'Aland Islands': 'AX',
    'Albania': 'AL',
    'Algeria': 'DZ',
    'American Samoa': 'AS',
    'Andorra': 'AD',
    'Angola': 'AO',
    'Anguilla': 'AI',
    'Antarctica': 'AQ',
    'Antigua And Barbuda': 'AG',
    'Argentina': 'AR',
    'Armenia': 'AM',
    'Aruba': 'AW',
    'Australia': 'AU',
    'Austria': 'AT',
    'Azerbaijan': 'AZ',
    'Bahamas': 'BS',
    'Bahrain': 'BH',
    'Bangladesh': 'BD',
    'Barbados': 'BB',
    'Belarus': 'BY',
    'Belgium': 'BE',
    'Belize': 'BZ',
    'Benin': 'BJ',
    'Bermuda': 'BM',
    'Bhutan': 'BT',
    'Bolivia': 'BO',
    'Bosnia And Herzegovina': 'BA',
    'Botswana': 'BW',
    'Bouvet Island': 'BV',
    'Brazil': 'BR',
    'British Indian Ocean Territory': 'IO',
    'Brunei Darussalam': 'BN',
    'Bulgaria': 'BG',
    'Burkina Faso': 'BF',
    'Burundi': 'BI',
    'Cambodia': 'KH',
    'Cameroon': 'CM',
    'Canada': 'CA',
    'Cape Verde': 'CV',
    'Cayman Islands': 'KY',
    'Central African Republic': 'CF',
    'Chad': 'TD',
    'Chile': 'CL',
    'China': 'CN',
    'Christmas Island': 'CX',
    'Cocos (Keeling) Islands': 'CC',
    'Colombia': 'CO',
    'Comoros': 'KM',
    'Congo': 'CG',
    'Congo, Democratic Republic': 'CD',
    'Cook Islands': 'CK',
    'Costa Rica': 'CR',
    'Cote D\'Ivoire': 'CI',
    'Croatia': 'HR',
    'Cuba': 'CU',
    'Cyprus': 'CY',
    'Czech Republic': 'CZ',
    'Denmark': 'DK',
    'Djibouti': 'DJ',
    'Dominica': 'DM',
    'Dominican Republic': 'DO',
    'Ecuador': 'EC',
    'Egypt': 'EG',
    'El Salvador': 'SV',
    'Equatorial Guinea': 'GQ',
    'Eritrea': 'ER',
    'Estonia': 'EE',
    'Ethiopia': 'ET',
    'Falkland Islands (Malvinas)': 'FK',
    'Faroe Islands': 'FO',
    'Fiji': 'FJ',
    'Finland': 'FI',
    'France': 'FR',
    'French Guiana': 'GF',
    'French Polynesia': 'PF',
    'French Southern Territories': 'TF',
    'Gabon': 'GA',
    'Gambia': 'GM',
    'Georgia': 'GE',
    'Germany': 'DE',
    'Ghana': 'GH',
    'Gibraltar': 'GI',
    'Greece': 'GR',
    'Greenland': 'GL',
    'Grenada': 'GD',
    'Guadeloupe': 'GP',
    'Guam': 'GU',
    'Guatemala': 'GT',
    'Guernsey': 'GG',
    'Guinea': 'GN',
    'Guinea-Bissau': 'GW',
    'Guyana': 'GY',
    'Haiti': 'HT',
    'Heard Island & Mcdonald Islands': 'HM',
    'Holy See (Vatican City State)': 'VA',
    'Honduras': 'HN',
    'Hong Kong': 'HK',
    'Hungary': 'HU',
    'Iceland': 'IS',
    'India': 'IN',
    'Indonesia': 'ID',
    'Iran, Islamic Republic Of': 'IR',
    'Iraq': 'IQ',
    'Ireland': 'IE',
    'Isle Of Man': 'IM',
    'Israel': 'IL',
    'Italy': 'IT',
    'Jamaica': 'JM',
    'Japan': 'JP',
    'Jersey': 'JE',
    'Jordan': 'JO',
    'Kazakhstan': 'KZ',
    'Kenya': 'KE',
    'Kiribati': 'KI',
    'Korea': 'KR',
    'Kuwait': 'KW',
    'Kyrgyzstan': 'KG',
    'Lao People\'s Democratic Republic': 'LA',
    'Latvia': 'LV',
    'Lebanon': 'LB',
    'Lesotho': 'LS',
    'Liberia': 'LR',
    'Libyan Arab Jamahiriya': 'LY',
    'Liechtenstein': 'LI',
    'Lithuania': 'LT',
    'Luxembourg': 'LU',
    'Macao': 'MO',
    'Macedonia': 'MK',
    'Madagascar': 'MG',
    'Malawi': 'MW',
    'Malaysia': 'MY',
    'Maldives': 'MV',
    'Mali': 'ML',
    'Malta': 'MT',
    'Marshall Islands': 'MH',
    'Martinique': 'MQ',
    'Mauritania': 'MR',
    'Mauritius': 'MU',
    'Mayotte': 'YT',
    'Mexico': 'MX',
    'Micronesia, Federated States Of': 'FM',
    'Moldova': 'MD',
    'Monaco': 'MC',
    'Mongolia': 'MN',
    'Montenegro': 'ME',
    'Montserrat': 'MS',
    'Morocco': 'MA',
    'Mozambique': 'MZ',
    'Myanmar': 'MM',
    'Namibia': 'NA',
    'Nauru': 'NR',
    'Nepal': 'NP',
    'Netherlands': 'NL',
    'Netherlands Antilles': 'AN',
    'New Caledonia': 'NC',
    'New Zealand': 'NZ',
    'Nicaragua': 'NI',
    'Niger': 'NE',
    'Nigeria': 'NG',
    'Niue': 'NU',
    'Norfolk Island': 'NF',
    'Northern Mariana Islands': 'MP',
    'Norway': 'NO',
    'Oman': 'OM',
    'Pakistan': 'PK',
    'Palau': 'PW',
    'Palestinian Territory, Occupied': 'PS',
    'Panama': 'PA',
    'Papua New Guinea': 'PG',
    'Paraguay': 'PY',
    'Peru': 'PE',
    'Philippines': 'PH',
    'Pitcairn': 'PN',
    'Poland': 'PL',
    'Portugal': 'PT',
    'Puerto Rico': 'PR',
    'Qatar': 'QA',
    'Reunion': 'RE',
    'Romania': 'RO',
    'Russian Federation': 'RU',
    'Rwanda': 'RW',
    'Saint Barthelemy': 'BL',
    'Saint Helena': 'SH',
    'Saint Kitts And Nevis': 'KN',
    'Saint Lucia': 'LC',
    'Saint Martin': 'MF',
    'Saint Pierre And Miquelon': 'PM',
    'Saint Vincent And Grenadines': 'VC',
    'Samoa': 'WS',
    'San Marino': 'SM',
    'Sao Tome And Principe': 'ST',
    'Saudi Arabia': 'SA',
    'Senegal': 'SN',
    'Serbia': 'RS',
    'Seychelles': 'SC',
    'Sierra Leone': 'SL',
    'Singapore': 'SG',
    'Slovakia': 'SK',
    'Slovenia': 'SI',
    'Solomon Islands': 'SB',
    'Somalia': 'SO',
    'South Africa': 'ZA',
    'South Georgia And Sandwich Isl.': 'GS',
    'Spain': 'ES',
    'Sri Lanka': 'LK',
    'Sudan': 'SD',
    'Suriname': 'SR',
    'Svalbard And Jan Mayen': 'SJ',
    'Swaziland': 'SZ',
    'Sweden': 'SE',
    'Switzerland': 'CH',
    'Syrian Arab Republic': 'SY',
    'Taiwan': 'TW',
    'Tajikistan': 'TJ',
    'Tanzania': 'TZ',
    'Thailand': 'TH',
    'Timor-Leste': 'TL',
    'Togo': 'TG',
    'Tokelau': 'TK',
    'Tonga': 'TO',
    'Trinidad And Tobago': 'TT',
    'Tunisia': 'TN',
    'Turkey': 'TR',
    'Turkmenistan': 'TM',
    'Turks And Caicos Islands': 'TC',
    'Tuvalu': 'TV',
    'Uganda': 'UG',
    'Ukraine': 'UA',
    'United Arab Emirates': 'AE',
    'United Kingdom': 'GB',
    'United States': 'US',
    'United States Outlying Islands': 'UM',
    'Uruguay': 'UY',
    'Uzbekistan': 'UZ',
    'Vanuatu': 'VU',
    'Venezuela': 'VE',
    'Viet Nam': 'VN',
    'Virgin Islands, British': 'VG',
    'Virgin Islands, U.S.': 'VI',
    'Wallis And Futuna': 'WF',
    'Western Sahara': 'EH',
    'Yemen': 'YE',
    'Zambia': 'ZM',
    'Zimbabwe': 'ZW',
    'North Macedonia': 'MK',
    'Češka': 'CZ',
  })

  function getCountryCode(country) {
    return countryList.value[country]
  }

  return {
    getCountryCode,
  }
}

export const useDate = () => {
  const months = {
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    fr: ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
  }

  const days = {
    en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    es: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'],
    fr: ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'],
  }

  return {
    months,
    days,
  }
}

export const useDebounce = () => {
  function debounce(func, timeout) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        func.apply(this, args)
      }, timeout)
    }
  }

  return {
    debounce,
  }
}

export const useEnhancedModal = () => {
  const isEnhanced = useState('modal-is-enhanced', () => false)

  return {
    isEnhanced,
  }
}

export const useForm = () => {
  const currentFormIndex = useState('form-index', () => -1)

  function setFormIndex(index) {
    currentFormIndex.value = index
  }

  return {
    currentFormIndex,
    setFormIndex,
  }
}

export const useIsLoggedIn = () => {
  // Refs
  const { hook } = useNuxtApp()
  const isLogged = useState('user-is-authenticated', () => false)

  // Methods
  function isUserLoggedIn() {
    return localStorage.ajs_user_id && localStorage.ajs_user_id !== null && localStorage.ajs_user_id !== 'null'
  }

  // Lifecycle Hooks
  hook('app:mounted', () => {
    isLogged.value = isUserLoggedIn()
  })

  hook('page:finish', () => {
    isLogged.value = isUserLoggedIn()
  })

  return {
    isLogged,
  }
}

export const useLdJson = (data) => {
  useHead(() => {
    return {
      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(data, null, ''),
        },
      ],
    }
  })
}

export const useMarkdown = () => {
  const { public: publicRuntimeConfig } = useRuntimeConfig()
  const $md = new MarkdownIt('default', {
    breaks: true,
    html: true,
    linkify: false,
    replaceLink: function (link) {
      if (
        publicRuntimeConfig.appBaseUrl?.includes('livestorm.io')
        && link.includes('app.livestorm.co')
      ) {
        return link.replace('app.livestorm.co', publicRuntimeConfig.appBaseUrl)
      }
      return link
    },
  })

  $md.use(milr)
  $md.use(mila, [
    {
      attrs: {
        rel: 'noopener',
      },
      pattern: /https?:\/\/livestorm\.co/,
    },
    {
      attrs: {
        rel: 'noopener nofollow',
        target: '_blank',
      },
      pattern: /https?:\/\/app\.livestorm\.co/,
    },
    {
      attrs: {
        rel: 'noopener',
        target: '_blank',
      },
      pattern: /https?:\/\/[^.\s]+\.livestorm\.co/,
    },
    {
      attrs: {
        rel: 'noopener',
        target: '_blank',
      },
      pattern: /https?:\/\/video-engagement\.org/,
    },
    {
      attrs: {
        rel: 'noopener nofollow',
        target: '_blank',
      },
      pattern: /https?:\/\/[^.\s]+\.*/,
    },
    {
      attrs: {
        rel: 'noopener nofollow',
        target: '_blank',
      },
      pattern: /mailto:[^.\s]+/,
    },
    {
      attrs: {
        rel: 'noopener',
      },
    },
  ])

  return {
    $md,
  }
}

export const useNavigation = () => {
  const isVisible = useState('navigation-is-visible', () => true)

  function hide() {
    isVisible.value = false
  }

  function show() {
    isVisible.value = true
  }

  return {
    hide,
    isVisible,
    show,
  }
}

export const useResource = () => {
  const resource = useState('resource', () => {})

  function setResource(value) {
    resource.value = value
  }

  return {
    resource,
    setResource,
  }
}

export const useScreenSize = () => {
  const dpr = ref(1)
  const height = ref(640)
  const width = ref(360)

  function onResize() {
    height.value = Math.min(document.documentElement.clientHeight, window.innerHeight)
    width.value = Math.min(document.documentElement.clientWidth, window.innerWidth)
  }

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
  })

  onMounted(() => {
    dpr.value = window.devicePixelRatio || 1
    window.addEventListener('resize', onResize)
    onResize()
  })

  return {
    dpr,
    height,
    width,
  }
}

export const useToolsBackground = () => {
  return 'data:text/html;base64,PGh0bWw+CiAgICAgICAgPGhlYWQ+CiAgICAgICAgICAgIDxtZXRhIG5hbWU9InZpZXdwb3J0IiBjb250ZW50PSJ3aWR0aD1kZXZpY2Utd2lkdGgsIGluaXRpYWwtc2NhbGU9MSI+CiAgICAgICAgICAgIDxzdHlsZT4KICAgICAgICAgICAgICAgIGh0bWwsIGJvZHl7CiAgICAgICAgICAgICAgICAgICAgbWFyZ2luOiAwOwogICAgICAgICAgICAgICAgICAgIHBhZGRpbmc6IDA7CiAgICAgICAgICAgICAgICAgICAgd2lkdGg6IDEwMCU7CiAgICAgICAgICAgICAgICAgICAgaGVpZ2h0OiAxMDAlOwogICAgICAgICAgICAgICAgICAgIGJvcmRlcjogMDsKICAgICAgICAgICAgICAgIH0KICAgICAgICAgICAgPC9zdHlsZT4KICAgICAgICAgICAgPHNjcmlwdCB0eXBlPSJpbXBvcnRtYXAiPgp7CiAgICAiaW1wb3J0cyI6IHsKICAgICAgICAicmVhY3QiOiAiaHR0cHM6Ly9jZG4uc2t5cGFjay5kZXYvcmVhY3RAMTguMC4yIiwKICAgICAgICAicmVhY3QtZG9tIjogImh0dHBzOi8vY2RuLnNreXBhY2suZGV2L3JlYWN0LWRvbUAxOC4wLjIiLAogICAgICAgICJ0aHJlZSI6ICJodHRwczovL2Nkbi5za3lwYWNrLmRldi90aHJlZUAwLjE0OC4wIiwKICAgICAgICAicmVhY3QtdGhyZWUvZmliZXIiOiAiaHR0cHM6Ly9jZG4uc2t5cGFjay5kZXYvQHJlYWN0LXRocmVlL2ZpYmVyQDcuMC4yNCIKICAgIH0KfQo8L3NjcmlwdD4KPHN0eWxlPgogICAgaHRtbCwgYm9keXsKICAgICAgICBtYXJnaW46IDA7CiAgICAgICAgcGFkZGluZzogMDsKICAgICAgICB3aWR0aDogMTAwJTsKICAgICAgICBoZWlnaHQ6IDEwMCU7CiAgICAgICAgYm9yZGVyOiAwOwogICAgfQogICAgLm50LWVtYmVkewogICAgICAgIHdpZHRoOiAxMDAlOwogICAgICAgIGhlaWdodDogMTAwJTsKICAgIH0KICAgIC5udC1lbWJlZCBjYW52YXN7CiAgICAgICAgd2lkdGg6IDEwMCU7CiAgICAgICAgaGVpZ2h0OiAxMDAlOwogICAgfQo8L3N0eWxlPgo8c2NyaXB0IHR5cGU9Im1vZHVsZSI+CiAgICBpbXBvcnQgUmVhY3QsIHt1c2VSZWYsdXNlTWVtb30gZnJvbSAncmVhY3QnOwogICAgaW1wb3J0IFJlYWN0RE9NIGZyb20gJ3JlYWN0LWRvbSc7CiAgICBpbXBvcnQgKiBhcyBUSFJFRSBmcm9tICd0aHJlZSc7CiAgICBpbXBvcnQge0NhbnZhcywgdXNlRnJhbWUsIHVzZVRocmVlfSBmcm9tICdyZWFjdC10aHJlZS9maWJlcic7CgogICAgbGV0IGVtYmVkUm9vdCA9IGRvY3VtZW50LmNyZWF0ZUVsZW1lbnQoJ2RpdicpOwogICAgZW1iZWRSb290LmNsYXNzTmFtZSA9ICJudC1lbWJlZCI7CiAgICBkb2N1bWVudC5ib2R5LmFwcGVuZENoaWxkKGVtYmVkUm9vdCk7CgogICAgY29uc3QgVGV4dHVyZU1lc2ggPSAoKSA9PiB7CiAgICAgICAgY29uc3QgbWVzaCA9IHVzZVJlZihudWxsKQogICAgICAgIHVzZUZyYW1lKHN0YXRlID0+IHsKICAgICAgICAgICAgY29uc3QgeyBjbG9jaywgbW91c2UsIGdsLCBzY2VuZSwgY2FtZXJhIH0gPSBzdGF0ZQogICAgICAgICAgICBpZihtZXNoLmN1cnJlbnQpewogICAgICAgICAgICAgICAgbWVzaC5jdXJyZW50Lm1hdGVyaWFsLnVuaWZvcm1zLnVfbW91c2UudmFsdWUgPSBbbW91c2UueCAvIDIgKyAwLjUsIG1vdXNlLnkgLyAyICsgMC41XQogICAgICAgICAgICAgICAgbWVzaC5jdXJyZW50Lm1hdGVyaWFsLnVuaWZvcm1zLnVfdGltZS52YWx1ZSA9IGNsb2NrLmdldEVsYXBzZWRUaW1lKCkKICAgICAgICAgICAgICAgIGxldCBjID0gZ2wuZG9tRWxlbWVudC5nZXRCb3VuZGluZ0NsaWVudFJlY3QoKQogICAgICAgICAgICAgICAgbWVzaC5jdXJyZW50Lm1hdGVyaWFsLnVuaWZvcm1zLnVfcmVzb2x1dGlvbi52YWx1ZSA9IFtjLndpZHRoLGMuaGVpZ2h0XQogICAgICAgICAgICB9CiAgICAgICAgfSkKICAgICAgICAKICAgICAgICByZXR1cm4gUmVhY3QuY3JlYXRlRWxlbWVudCgnbWVzaCcsCiAgICAgICAgICAgIHsKICAgICAgICAgICAgICAgIHJlZjptZXNoLAogICAgICAgICAgICAgICAgcG9zaXRpb246IFswLDAsMF0sCiAgICAgICAgICAgICAgICBzY2FsZTogMSwKICAgICAgICAgICAgICAgIHJvdGF0aW9uOiBbMCwwLDBdCiAgICAgICAgICAgIH0sCiAgICAgICAgICAgIFJlYWN0LmNyZWF0ZUVsZW1lbnQoJ3BsYW5lR2VvbWV0cnknLHthcmdzOlsyNDAsNTgwXX0pLCAKICAgICAgICAgICAgUmVhY3QuY3JlYXRlRWxlbWVudCgnc2hhZGVyTWF0ZXJpYWwnLHsKICAgICAgICAgICAgICAgIGZyYWdtZW50U2hhZGVyOiBgLy8gRnJhZ21lbnQgc2hhZGVyCgoKCi8vIFVuaWZvcm1zCgp1bmlmb3JtIHZlYzIgdV9yZXNvbHV0aW9uOwoKdW5pZm9ybSB2ZWMyIHVfbW91c2U7Cgp1bmlmb3JtIGZsb2F0IHVfdGltZTsKCnVuaWZvcm0gdmVjNCB1X2NvbG9yc1syXTsKCnVuaWZvcm0gZmxvYXQgdV9pbnRlbnNpdHk7Cgp1bmlmb3JtIGZsb2F0IHVfcmF5czsKCnVuaWZvcm0gZmxvYXQgdV9yZWFjaDsKCgoKCgojaWZuZGVmIEZOQ19NT0QyODkKI2RlZmluZSBGTkNfTU9EMjg5CgpmbG9hdCBtb2QyODkoY29uc3QgaW4gZmxvYXQgeCkgeyByZXR1cm4geCAtIGZsb29yKHggKiAoMS4gLyAyODkuKSkgKiAyODkuOyB9CnZlYzIgbW9kMjg5KGNvbnN0IGluIHZlYzIgeCkgeyByZXR1cm4geCAtIGZsb29yKHggKiAoMS4gLyAyODkuKSkgKiAyODkuOyB9CnZlYzMgbW9kMjg5KGNvbnN0IGluIHZlYzMgeCkgeyByZXR1cm4geCAtIGZsb29yKHggKiAoMS4gLyAyODkuKSkgKiAyODkuOyB9CnZlYzQgbW9kMjg5KGNvbnN0IGluIHZlYzQgeCkgeyByZXR1cm4geCAtIGZsb29yKHggKiAoMS4gLyAyODkuKSkgKiAyODkuOyB9CgojZW5kaWYKCgoKCgojaWZuZGVmIEZOQ19QRVJNVVRFCiNkZWZpbmUgRk5DX1BFUk1VVEUKCmZsb2F0IHBlcm11dGUoY29uc3QgaW4gZmxvYXQgeCkgeyByZXR1cm4gbW9kMjg5KCgoeCAqIDM0LjApICsgMS4wKSAqIHgpOyB9CnZlYzIgcGVybXV0ZShjb25zdCBpbiB2ZWMyIHgpIHsgcmV0dXJuIG1vZDI4OSgoKHggKiAzNC4wKSArIDEuMCkgKiB4KTsgfQp2ZWMzIHBlcm11dGUoY29uc3QgaW4gdmVjMyB4KSB7IHJldHVybiBtb2QyODkoKCh4ICogMzQuMCkgKyAxLjApICogeCk7IH0KdmVjNCBwZXJtdXRlKGNvbnN0IGluIHZlYzQgeCkgeyByZXR1cm4gbW9kMjg5KCgoeCAqIDM0LjApICsgMS4wKSAqIHgpOyB9CgojZW5kaWYKCgoKI2lmbmRlZiBGTkNfVEFZTE9SSU5WU1FSVAojZGVmaW5lIEZOQ19UQVlMT1JJTlZTUVJUCmZsb2F0IHRheWxvckludlNxcnQoaW4gZmxvYXQgcikgeyByZXR1cm4gMS43OTI4NDI5MTQwMDE1OSAtIDAuODUzNzM0NzIwOTUzMTQgKiByOyB9CnZlYzIgdGF5bG9ySW52U3FydChpbiB2ZWMyIHIpIHsgcmV0dXJuIDEuNzkyODQyOTE0MDAxNTkgLSAwLjg1MzczNDcyMDk1MzE0ICogcjsgfQp2ZWMzIHRheWxvckludlNxcnQoaW4gdmVjMyByKSB7IHJldHVybiAxLjc5Mjg0MjkxNDAwMTU5IC0gMC44NTM3MzQ3MjA5NTMxNCAqIHI7IH0KdmVjNCB0YXlsb3JJbnZTcXJ0KGluIHZlYzQgcikgeyByZXR1cm4gMS43OTI4NDI5MTQwMDE1OSAtIDAuODUzNzM0NzIwOTUzMTQgKiByOyB9CiNlbmRpZgoKCiNpZm5kZWYgRk5DX1FVSU5USUMKI2RlZmluZSBGTkNfUVVJTlRJQyAKCmZsb2F0IHF1aW50aWMoY29uc3QgaW4gZmxvYXQgdikgeyByZXR1cm4gdip2KnYqKHYqKHYqNi4wLTE1LjApKzEwLjApOyB9CnZlYzIgIHF1aW50aWMoY29uc3QgaW4gdmVjMiB2KSAgeyByZXR1cm4gdip2KnYqKHYqKHYqNi4wLTE1LjApKzEwLjApOyB9CnZlYzMgIHF1aW50aWMoY29uc3QgaW4gdmVjMyB2KSAgeyByZXR1cm4gdip2KnYqKHYqKHYqNi4wLTE1LjApKzEwLjApOyB9CnZlYzQgIHF1aW50aWMoY29uc3QgaW4gdmVjNCB2KSAgeyByZXR1cm4gdip2KnYqKHYqKHYqNi4wLTE1LjApKzEwLjApOyB9CgojZW5kaWYKCgoKI2lmbmRlZiBGTkNfUE5PSVNFCiNkZWZpbmUgRk5DX1BOT0lTRQoKZmxvYXQgcG5vaXNlKGluIHZlYzIgUCwgaW4gdmVjMiByZXApIHsKICAgIHZlYzQgUGkgPSBmbG9vcihQLnh5eHkpICsgdmVjNCgwLjAsIDAuMCwgMS4wLCAxLjApOwogICAgdmVjNCBQZiA9IGZyYWN0KFAueHl4eSkgLSB2ZWM0KDAuMCwgMC4wLCAxLjAsIDEuMCk7CiAgICBQaSA9IG1vZChQaSwgcmVwLnh5eHkpOyAKICAgIFBpID0gbW9kMjg5KFBpKTsgICAgICAgIAogICAgdmVjNCBpeCA9IFBpLnh6eHo7CiAgICB2ZWM0IGl5ID0gUGkueXl3dzsKICAgIHZlYzQgZnggPSBQZi54enh6OwogICAgdmVjNCBmeSA9IFBmLnl5d3c7CgogICAgdmVjNCBpID0gcGVybXV0ZShwZXJtdXRlKGl4KSArIGl5KTsKCiAgICB2ZWM0IGd4ID0gZnJhY3QoaSAqICgxLjAgLyA0MS4wKSkgKiAyLjAgLSAxLjAgOwogICAgdmVjNCBneSA9IGFicyhneCkgLSAwLjUgOwogICAgdmVjNCB0eCA9IGZsb29yKGd4ICsgMC41KTsKICAgIGd4ID0gZ3ggLSB0eDsKCiAgICB2ZWMyIGcwMCA9IHZlYzIoZ3gueCxneS54KTsKICAgIHZlYzIgZzEwID0gdmVjMihneC55LGd5LnkpOwogICAgdmVjMiBnMDEgPSB2ZWMyKGd4LnosZ3kueik7CiAgICB2ZWMyIGcxMSA9IHZlYzIoZ3gudyxneS53KTsKCiAgICB2ZWM0IG5vcm0gPSB0YXlsb3JJbnZTcXJ0KHZlYzQoZG90KGcwMCwgZzAwKSwgZG90KGcwMSwgZzAxKSwgZG90KGcxMCwgZzEwKSwgZG90KGcxMSwgZzExKSkpOwogICAgZzAwICo9IG5vcm0ueDsKICAgIGcwMSAqPSBub3JtLnk7CiAgICBnMTAgKj0gbm9ybS56OwogICAgZzExICo9IG5vcm0udzsKCiAgICBmbG9hdCBuMDAgPSBkb3QoZzAwLCB2ZWMyKGZ4LngsIGZ5LngpKTsKICAgIGZsb2F0IG4xMCA9IGRvdChnMTAsIHZlYzIoZngueSwgZnkueSkpOwogICAgZmxvYXQgbjAxID0gZG90KGcwMSwgdmVjMihmeC56LCBmeS56KSk7CiAgICBmbG9hdCBuMTEgPSBkb3QoZzExLCB2ZWMyKGZ4LncsIGZ5LncpKTsKCiAgICB2ZWMyIGZhZGVfeHkgPSBxdWludGljKFBmLnh5KTsKICAgIHZlYzIgbl94ID0gbWl4KHZlYzIobjAwLCBuMDEpLCB2ZWMyKG4xMCwgbjExKSwgZmFkZV94eS54KTsKICAgIGZsb2F0IG5feHkgPSBtaXgobl94LngsIG5feC55LCBmYWRlX3h5LnkpOwogICAgcmV0dXJuIDIuMyAqIG5feHk7Cn0KCmZsb2F0IHBub2lzZShpbiB2ZWMzIFAsIGluIHZlYzMgcmVwKSB7CiAgICB2ZWMzIFBpMCA9IG1vZChmbG9vcihQKSwgcmVwKTsgCiAgICB2ZWMzIFBpMSA9IG1vZChQaTAgKyB2ZWMzKDEuMCksIHJlcCk7IAogICAgUGkwID0gbW9kMjg5KFBpMCk7CiAgICBQaTEgPSBtb2QyODkoUGkxKTsKICAgIHZlYzMgUGYwID0gZnJhY3QoUCk7IAogICAgdmVjMyBQZjEgPSBQZjAgLSB2ZWMzKDEuMCk7IAogICAgdmVjNCBpeCA9IHZlYzQoUGkwLngsIFBpMS54LCBQaTAueCwgUGkxLngpOwogICAgdmVjNCBpeSA9IHZlYzQoUGkwLnl5LCBQaTEueXkpOwogICAgdmVjNCBpejAgPSBQaTAuenp6ejsKICAgIHZlYzQgaXoxID0gUGkxLnp6eno7CgogICAgdmVjNCBpeHkgPSBwZXJtdXRlKHBlcm11dGUoaXgpICsgaXkpOwogICAgdmVjNCBpeHkwID0gcGVybXV0ZShpeHkgKyBpejApOwogICAgdmVjNCBpeHkxID0gcGVybXV0ZShpeHkgKyBpejEpOwoKICAgIHZlYzQgZ3gwID0gaXh5MCAqICgxLjAgLyA3LjApOwogICAgdmVjNCBneTAgPSBmcmFjdChmbG9vcihneDApICogKDEuMCAvIDcuMCkpIC0gMC41OwogICAgZ3gwID0gZnJhY3QoZ3gwKTsKICAgIHZlYzQgZ3owID0gdmVjNCgwLjUpIC0gYWJzKGd4MCkgLSBhYnMoZ3kwKTsKICAgIHZlYzQgc3owID0gc3RlcChnejAsIHZlYzQoMC4wKSk7CiAgICBneDAgLT0gc3owICogKHN0ZXAoMC4wLCBneDApIC0gMC41KTsKICAgIGd5MCAtPSBzejAgKiAoc3RlcCgwLjAsIGd5MCkgLSAwLjUpOwoKICAgIHZlYzQgZ3gxID0gaXh5MSAqICgxLjAgLyA3LjApOwogICAgdmVjNCBneTEgPSBmcmFjdChmbG9vcihneDEpICogKDEuMCAvIDcuMCkpIC0gMC41OwogICAgZ3gxID0gZnJhY3QoZ3gxKTsKICAgIHZlYzQgZ3oxID0gdmVjNCgwLjUpIC0gYWJzKGd4MSkgLSBhYnMoZ3kxKTsKICAgIHZlYzQgc3oxID0gc3RlcChnejEsIHZlYzQoMC4wKSk7CiAgICBneDEgLT0gc3oxICogKHN0ZXAoMC4wLCBneDEpIC0gMC41KTsKICAgIGd5MSAtPSBzejEgKiAoc3RlcCgwLjAsIGd5MSkgLSAwLjUpOwoKICAgIHZlYzMgZzAwMCA9IHZlYzMoZ3gwLngsZ3kwLngsZ3owLngpOwogICAgdmVjMyBnMTAwID0gdmVjMyhneDAueSxneTAueSxnejAueSk7CiAgICB2ZWMzIGcwMTAgPSB2ZWMzKGd4MC56LGd5MC56LGd6MC56KTsKICAgIHZlYzMgZzExMCA9IHZlYzMoZ3gwLncsZ3kwLncsZ3owLncpOwogICAgdmVjMyBnMDAxID0gdmVjMyhneDEueCxneTEueCxnejEueCk7CiAgICB2ZWMzIGcxMDEgPSB2ZWMzKGd4MS55LGd5MS55LGd6MS55KTsKICAgIHZlYzMgZzAxMSA9IHZlYzMoZ3gxLnosZ3kxLnosZ3oxLnopOwogICAgdmVjMyBnMTExID0gdmVjMyhneDEudyxneTEudyxnejEudyk7CgogICAgdmVjNCBub3JtMCA9IHRheWxvckludlNxcnQodmVjNChkb3QoZzAwMCwgZzAwMCksIGRvdChnMDEwLCBnMDEwKSwgZG90KGcxMDAsIGcxMDApLCBkb3QoZzExMCwgZzExMCkpKTsKICAgIGcwMDAgKj0gbm9ybTAueDsKICAgIGcwMTAgKj0gbm9ybTAueTsKICAgIGcxMDAgKj0gbm9ybTAuejsKICAgIGcxMTAgKj0gbm9ybTAudzsKICAgIHZlYzQgbm9ybTEgPSB0YXlsb3JJbnZTcXJ0KHZlYzQoZG90KGcwMDEsIGcwMDEpLCBkb3QoZzAxMSwgZzAxMSksIGRvdChnMTAxLCBnMTAxKSwgZG90KGcxMTEsIGcxMTEpKSk7CiAgICBnMDAxICo9IG5vcm0xLng7CiAgICBnMDExICo9IG5vcm0xLnk7CiAgICBnMTAxICo9IG5vcm0xLno7CiAgICBnMTExICo9IG5vcm0xLnc7CgogICAgZmxvYXQgbjAwMCA9IGRvdChnMDAwLCBQZjApOwogICAgZmxvYXQgbjEwMCA9IGRvdChnMTAwLCB2ZWMzKFBmMS54LCBQZjAueXopKTsKICAgIGZsb2F0IG4wMTAgPSBkb3QoZzAxMCwgdmVjMyhQZjAueCwgUGYxLnksIFBmMC56KSk7CiAgICBmbG9hdCBuMTEwID0gZG90KGcxMTAsIHZlYzMoUGYxLnh5LCBQZjAueikpOwogICAgZmxvYXQgbjAwMSA9IGRvdChnMDAxLCB2ZWMzKFBmMC54eSwgUGYxLnopKTsKICAgIGZsb2F0IG4xMDEgPSBkb3QoZzEwMSwgdmVjMyhQZjEueCwgUGYwLnksIFBmMS56KSk7CiAgICBmbG9hdCBuMDExID0gZG90KGcwMTEsIHZlYzMoUGYwLngsIFBmMS55eikpOwogICAgZmxvYXQgbjExMSA9IGRvdChnMTExLCBQZjEpOwoKICAgIHZlYzMgZmFkZV94eXogPSBxdWludGljKFBmMCk7CiAgICB2ZWM0IG5feiA9IG1peCh2ZWM0KG4wMDAsIG4xMDAsIG4wMTAsIG4xMTApLCB2ZWM0KG4wMDEsIG4xMDEsIG4wMTEsIG4xMTEpLCBmYWRlX3h5ei56KTsKICAgIHZlYzIgbl95eiA9IG1peChuX3oueHksIG5fei56dywgZmFkZV94eXoueSk7CiAgICBmbG9hdCBuX3h5eiA9IG1peChuX3l6LngsIG5feXoueSwgZmFkZV94eXoueCk7CiAgICByZXR1cm4gMi4yICogbl94eXo7Cn0KCmZsb2F0IHBub2lzZShpbiB2ZWM0IFAsIGluIHZlYzQgcmVwKSB7CiAgICB2ZWM0IFBpMCA9IG1vZChmbG9vcihQKSwgcmVwKTsgCiAgICB2ZWM0IFBpMSA9IG1vZChQaTAgKyAxLjAsIHJlcCk7IAogICAgUGkwID0gbW9kMjg5KFBpMCk7CiAgICBQaTEgPSBtb2QyODkoUGkxKTsKICAgIHZlYzQgUGYwID0gZnJhY3QoUCk7IAogICAgdmVjNCBQZjEgPSBQZjAgLSAxLjA7IAogICAgdmVjNCBpeCA9IHZlYzQoUGkwLngsIFBpMS54LCBQaTAueCwgUGkxLngpOwogICAgdmVjNCBpeSA9IHZlYzQoUGkwLnl5LCBQaTEueXkpOwogICAgdmVjNCBpejAgPSB2ZWM0KFBpMC56enp6KTsKICAgIHZlYzQgaXoxID0gdmVjNChQaTEuenp6eik7CiAgICB2ZWM0IGl3MCA9IHZlYzQoUGkwLnd3d3cpOwogICAgdmVjNCBpdzEgPSB2ZWM0KFBpMS53d3d3KTsKCiAgICB2ZWM0IGl4eSA9IHBlcm11dGUocGVybXV0ZShpeCkgKyBpeSk7CiAgICB2ZWM0IGl4eTAgPSBwZXJtdXRlKGl4eSArIGl6MCk7CiAgICB2ZWM0IGl4eTEgPSBwZXJtdXRlKGl4eSArIGl6MSk7CiAgICB2ZWM0IGl4eTAwID0gcGVybXV0ZShpeHkwICsgaXcwKTsKICAgIHZlYzQgaXh5MDEgPSBwZXJtdXRlKGl4eTAgKyBpdzEpOwogICAgdmVjNCBpeHkxMCA9IHBlcm11dGUoaXh5MSArIGl3MCk7CiAgICB2ZWM0IGl4eTExID0gcGVybXV0ZShpeHkxICsgaXcxKTsKCiAgICB2ZWM0IGd4MDAgPSBpeHkwMCAqICgxLjAgLyA3LjApOwogICAgdmVjNCBneTAwID0gZmxvb3IoZ3gwMCkgKiAoMS4wIC8gNy4wKTsKICAgIHZlYzQgZ3owMCA9IGZsb29yKGd5MDApICogKDEuMCAvIDYuMCk7CiAgICBneDAwID0gZnJhY3QoZ3gwMCkgLSAwLjU7CiAgICBneTAwID0gZnJhY3QoZ3kwMCkgLSAwLjU7CiAgICBnejAwID0gZnJhY3QoZ3owMCkgLSAwLjU7CiAgICB2ZWM0IGd3MDAgPSB2ZWM0KDAuNzUpIC0gYWJzKGd4MDApIC0gYWJzKGd5MDApIC0gYWJzKGd6MDApOwogICAgdmVjNCBzdzAwID0gc3RlcChndzAwLCB2ZWM0KDAuMCkpOwogICAgZ3gwMCAtPSBzdzAwICogKHN0ZXAoMC4wLCBneDAwKSAtIDAuNSk7CiAgICBneTAwIC09IHN3MDAgKiAoc3RlcCgwLjAsIGd5MDApIC0gMC41KTsKCiAgICB2ZWM0IGd4MDEgPSBpeHkwMSAqICgxLjAgLyA3LjApOwogICAgdmVjNCBneTAxID0gZmxvb3IoZ3gwMSkgKiAoMS4wIC8gNy4wKTsKICAgIHZlYzQgZ3owMSA9IGZsb29yKGd5MDEpICogKDEuMCAvIDYuMCk7CiAgICBneDAxID0gZnJhY3QoZ3gwMSkgLSAwLjU7CiAgICBneTAxID0gZnJhY3QoZ3kwMSkgLSAwLjU7CiAgICBnejAxID0gZnJhY3QoZ3owMSkgLSAwLjU7CiAgICB2ZWM0IGd3MDEgPSB2ZWM0KDAuNzUpIC0gYWJzKGd4MDEpIC0gYWJzKGd5MDEpIC0gYWJzKGd6MDEpOwogICAgdmVjNCBzdzAxID0gc3RlcChndzAxLCB2ZWM0KDAuMCkpOwogICAgZ3gwMSAtPSBzdzAxICogKHN0ZXAoMC4wLCBneDAxKSAtIDAuNSk7CiAgICBneTAxIC09IHN3MDEgKiAoc3RlcCgwLjAsIGd5MDEpIC0gMC41KTsKCiAgICB2ZWM0IGd4MTAgPSBpeHkxMCAqICgxLjAgLyA3LjApOwogICAgdmVjNCBneTEwID0gZmxvb3IoZ3gxMCkgKiAoMS4wIC8gNy4wKTsKICAgIHZlYzQgZ3oxMCA9IGZsb29yKGd5MTApICogKDEuMCAvIDYuMCk7CiAgICBneDEwID0gZnJhY3QoZ3gxMCkgLSAwLjU7CiAgICBneTEwID0gZnJhY3QoZ3kxMCkgLSAwLjU7CiAgICBnejEwID0gZnJhY3QoZ3oxMCkgLSAwLjU7CiAgICB2ZWM0IGd3MTAgPSB2ZWM0KDAuNzUpIC0gYWJzKGd4MTApIC0gYWJzKGd5MTApIC0gYWJzKGd6MTApOwogICAgdmVjNCBzdzEwID0gc3RlcChndzEwLCB2ZWM0KDAuMCkpOwogICAgZ3gxMCAtPSBzdzEwICogKHN0ZXAoMC4wLCBneDEwKSAtIDAuNSk7CiAgICBneTEwIC09IHN3MTAgKiAoc3RlcCgwLjAsIGd5MTApIC0gMC41KTsKCiAgICB2ZWM0IGd4MTEgPSBpeHkxMSAqICgxLjAgLyA3LjApOwogICAgdmVjNCBneTExID0gZmxvb3IoZ3gxMSkgKiAoMS4wIC8gNy4wKTsKICAgIHZlYzQgZ3oxMSA9IGZsb29yKGd5MTEpICogKDEuMCAvIDYuMCk7CiAgICBneDExID0gZnJhY3QoZ3gxMSkgLSAwLjU7CiAgICBneTExID0gZnJhY3QoZ3kxMSkgLSAwLjU7CiAgICBnejExID0gZnJhY3QoZ3oxMSkgLSAwLjU7CiAgICB2ZWM0IGd3MTEgPSB2ZWM0KDAuNzUpIC0gYWJzKGd4MTEpIC0gYWJzKGd5MTEpIC0gYWJzKGd6MTEpOwogICAgdmVjNCBzdzExID0gc3RlcChndzExLCB2ZWM0KDAuMCkpOwogICAgZ3gxMSAtPSBzdzExICogKHN0ZXAoMC4wLCBneDExKSAtIDAuNSk7CiAgICBneTExIC09IHN3MTEgKiAoc3RlcCgwLjAsIGd5MTEpIC0gMC41KTsKCiAgICB2ZWM0IGcwMDAwID0gdmVjNChneDAwLngsZ3kwMC54LGd6MDAueCxndzAwLngpOwogICAgdmVjNCBnMTAwMCA9IHZlYzQoZ3gwMC55LGd5MDAueSxnejAwLnksZ3cwMC55KTsKICAgIHZlYzQgZzAxMDAgPSB2ZWM0KGd4MDAueixneTAwLnosZ3owMC56LGd3MDAueik7CiAgICB2ZWM0IGcxMTAwID0gdmVjNChneDAwLncsZ3kwMC53LGd6MDAudyxndzAwLncpOwogICAgdmVjNCBnMDAxMCA9IHZlYzQoZ3gxMC54LGd5MTAueCxnejEwLngsZ3cxMC54KTsKICAgIHZlYzQgZzEwMTAgPSB2ZWM0KGd4MTAueSxneTEwLnksZ3oxMC55LGd3MTAueSk7CiAgICB2ZWM0IGcwMTEwID0gdmVjNChneDEwLnosZ3kxMC56LGd6MTAueixndzEwLnopOwogICAgdmVjNCBnMTExMCA9IHZlYzQoZ3gxMC53LGd5MTAudyxnejEwLncsZ3cxMC53KTsKICAgIHZlYzQgZzAwMDEgPSB2ZWM0KGd4MDEueCxneTAxLngsZ3owMS54LGd3MDEueCk7CiAgICB2ZWM0IGcxMDAxID0gdmVjNChneDAxLnksZ3kwMS55LGd6MDEueSxndzAxLnkpOwogICAgdmVjNCBnMDEwMSA9IHZlYzQoZ3gwMS56LGd5MDEueixnejAxLnosZ3cwMS56KTsKICAgIHZlYzQgZzExMDEgPSB2ZWM0KGd4MDEudyxneTAxLncsZ3owMS53LGd3MDEudyk7CiAgICB2ZWM0IGcwMDExID0gdmVjNChneDExLngsZ3kxMS54LGd6MTEueCxndzExLngpOwogICAgdmVjNCBnMTAxMSA9IHZlYzQoZ3gxMS55LGd5MTEueSxnejExLnksZ3cxMS55KTsKICAgIHZlYzQgZzAxMTEgPSB2ZWM0KGd4MTEueixneTExLnosZ3oxMS56LGd3MTEueik7CiAgICB2ZWM0IGcxMTExID0gdmVjNChneDExLncsZ3kxMS53LGd6MTEudyxndzExLncpOwoKICAgIHZlYzQgbm9ybTAwID0gdGF5bG9ySW52U3FydCh2ZWM0KGRvdChnMDAwMCwgZzAwMDApLCBkb3QoZzAxMDAsIGcwMTAwKSwgZG90KGcxMDAwLCBnMTAwMCksIGRvdChnMTEwMCwgZzExMDApKSk7CiAgICBnMDAwMCAqPSBub3JtMDAueDsKICAgIGcwMTAwICo9IG5vcm0wMC55OwogICAgZzEwMDAgKj0gbm9ybTAwLno7CiAgICBnMTEwMCAqPSBub3JtMDAudzsKCiAgICB2ZWM0IG5vcm0wMSA9IHRheWxvckludlNxcnQodmVjNChkb3QoZzAwMDEsIGcwMDAxKSwgZG90KGcwMTAxLCBnMDEwMSksIGRvdChnMTAwMSwgZzEwMDEpLCBkb3QoZzExMDEsIGcxMTAxKSkpOwogICAgZzAwMDEgKj0gbm9ybTAxLng7CiAgICBnMDEwMSAqPSBub3JtMDEueTsKICAgIGcxMDAxICo9IG5vcm0wMS56OwogICAgZzExMDEgKj0gbm9ybTAxLnc7CgogICAgdmVjNCBub3JtMTAgPSB0YXlsb3JJbnZTcXJ0KHZlYzQoZG90KGcwMDEwLCBnMDAxMCksIGRvdChnMDExMCwgZzAxMTApLCBkb3QoZzEwMTAsIGcxMDEwKSwgZG90KGcxMTEwLCBnMTExMCkpKTsKICAgIGcwMDEwICo9IG5vcm0xMC54OwogICAgZzAxMTAgKj0gbm9ybTEwLnk7CiAgICBnMTAxMCAqPSBub3JtMTAuejsKICAgIGcxMTEwICo9IG5vcm0xMC53OwoKICAgIHZlYzQgbm9ybTExID0gdGF5bG9ySW52U3FydCh2ZWM0KGRvdChnMDAxMSwgZzAwMTEpLCBkb3QoZzAxMTEsIGcwMTExKSwgZG90KGcxMDExLCBnMTAxMSksIGRvdChnMTExMSwgZzExMTEpKSk7CiAgICBnMDAxMSAqPSBub3JtMTEueDsKICAgIGcwMTExICo9IG5vcm0xMS55OwogICAgZzEwMTEgKj0gbm9ybTExLno7CiAgICBnMTExMSAqPSBub3JtMTEudzsKCiAgICBmbG9hdCBuMDAwMCA9IGRvdChnMDAwMCwgUGYwKTsKICAgIGZsb2F0IG4xMDAwID0gZG90KGcxMDAwLCB2ZWM0KFBmMS54LCBQZjAueXp3KSk7CiAgICBmbG9hdCBuMDEwMCA9IGRvdChnMDEwMCwgdmVjNChQZjAueCwgUGYxLnksIFBmMC56dykpOwogICAgZmxvYXQgbjExMDAgPSBkb3QoZzExMDAsIHZlYzQoUGYxLnh5LCBQZjAuencpKTsKICAgIGZsb2F0IG4wMDEwID0gZG90KGcwMDEwLCB2ZWM0KFBmMC54eSwgUGYxLnosIFBmMC53KSk7CiAgICBmbG9hdCBuMTAxMCA9IGRvdChnMTAxMCwgdmVjNChQZjEueCwgUGYwLnksIFBmMS56LCBQZjAudykpOwogICAgZmxvYXQgbjAxMTAgPSBkb3QoZzAxMTAsIHZlYzQoUGYwLngsIFBmMS55eiwgUGYwLncpKTsKICAgIGZsb2F0IG4xMTEwID0gZG90KGcxMTEwLCB2ZWM0KFBmMS54eXosIFBmMC53KSk7CiAgICBmbG9hdCBuMDAwMSA9IGRvdChnMDAwMSwgdmVjNChQZjAueHl6LCBQZjEudykpOwogICAgZmxvYXQgbjEwMDEgPSBkb3QoZzEwMDEsIHZlYzQoUGYxLngsIFBmMC55eiwgUGYxLncpKTsKICAgIGZsb2F0IG4wMTAxID0gZG90KGcwMTAxLCB2ZWM0KFBmMC54LCBQZjEueSwgUGYwLnosIFBmMS53KSk7CiAgICBmbG9hdCBuMTEwMSA9IGRvdChnMTEwMSwgdmVjNChQZjEueHksIFBmMC56LCBQZjEudykpOwogICAgZmxvYXQgbjAwMTEgPSBkb3QoZzAwMTEsIHZlYzQoUGYwLnh5LCBQZjEuencpKTsKICAgIGZsb2F0IG4xMDExID0gZG90KGcxMDExLCB2ZWM0KFBmMS54LCBQZjAueSwgUGYxLnp3KSk7CiAgICBmbG9hdCBuMDExMSA9IGRvdChnMDExMSwgdmVjNChQZjAueCwgUGYxLnl6dykpOwogICAgZmxvYXQgbjExMTEgPSBkb3QoZzExMTEsIFBmMSk7CgogICAgdmVjNCBmYWRlX3h5encgPSBxdWludGljKFBmMCk7CiAgICB2ZWM0IG5fMHcgPSBtaXgodmVjNChuMDAwMCwgbjEwMDAsIG4wMTAwLCBuMTEwMCksIHZlYzQobjAwMDEsIG4xMDAxLCBuMDEwMSwgbjExMDEpLCBmYWRlX3h5encudyk7CiAgICB2ZWM0IG5fMXcgPSBtaXgodmVjNChuMDAxMCwgbjEwMTAsIG4wMTEwLCBuMTExMCksIHZlYzQobjAwMTEsIG4xMDExLCBuMDExMSwgbjExMTEpLCBmYWRlX3h5encudyk7CiAgICB2ZWM0IG5fencgPSBtaXgobl8wdywgbl8xdywgZmFkZV94eXp3LnopOwogICAgdmVjMiBuX3l6dyA9IG1peChuX3p3Lnh5LCBuX3p3Lnp3LCBmYWRlX3h5encueSk7CiAgICBmbG9hdCBuX3h5encgPSBtaXgobl95encueCwgbl95encueSwgZmFkZV94eXp3LngpOwogICAgcmV0dXJuIDIuMiAqIG5feHl6dzsKfQojZW5kaWYKCgoKCgoKI2lmbmRlZiBTUkdCX0VQU0lMT04gCiNkZWZpbmUgU1JHQl9FUFNJTE9OIDAuMDAwMDAwMDEKI2VuZGlmCgojaWZuZGVmIEZOQ19TUkdCMlJHQgojZGVmaW5lIEZOQ19TUkdCMlJHQgoKCgoKZmxvYXQgc3JnYjJyZ2IoZmxvYXQgY2hhbm5lbCkgewogICAgcmV0dXJuIChjaGFubmVsIDwgMC4wNDA0NSkgPyBjaGFubmVsICogMC4wNzczOTkzODA4IDogcG93KChjaGFubmVsICsgMC4wNTUpICogMC45NDc4NjcyOTg1NzgxOTksIDIuNCk7Cn0KCnZlYzMgc3JnYjJyZ2IodmVjMyBzcmdiKSB7CiAgICByZXR1cm4gdmVjMyhzcmdiMnJnYihzcmdiLnIgKyBTUkdCX0VQU0lMT04pLCAKICAgICAgICAgICAgICAgIHNyZ2IycmdiKHNyZ2IuZyArIFNSR0JfRVBTSUxPTiksICAgICAgICAgICAgICAgICBzcmdiMnJnYihzcmdiLmIgKyBTUkdCX0VQU0lMT04pKTsKfQoKdmVjNCBzcmdiMnJnYih2ZWM0IHNyZ2IpIHsKICAgIHJldHVybiB2ZWM0KHNyZ2IycmdiKHNyZ2IucmdiKSwgc3JnYi5hKTsKfQoKI2VuZGlmCgoKI2lmICFkZWZpbmVkKEZOQ19TQVRVUkFURSkgJiYgIWRlZmluZWQoc2F0dXJhdGUpCiNkZWZpbmUgRk5DX1NBVFVSQVRFCiNkZWZpbmUgc2F0dXJhdGUoeCkgY2xhbXAoeCwgMC4wLCAxLjApCiNlbmRpZgoKCgojaWZuZGVmIFNSR0JfRVBTSUxPTiAKI2RlZmluZSBTUkdCX0VQU0lMT04gMC4wMDAwMDAwMQojZW5kaWYKCiNpZm5kZWYgRk5DX1JHQjJTUkdCCiNkZWZpbmUgRk5DX1JHQjJTUkdCCgoKZmxvYXQgcmdiMnNyZ2IoZmxvYXQgY2hhbm5lbCkgewogICAgcmV0dXJuIChjaGFubmVsIDwgMC4wMDMxMzA4KSA/IGNoYW5uZWwgKiAxMi45MiA6IDEuMDU1ICogcG93KGNoYW5uZWwsIDAuNDE2NjY2NjY2NjY2NjY2NykgLSAwLjA1NTsKfQoKdmVjMyByZ2Iyc3JnYih2ZWMzIHJnYikgewogICAgcmV0dXJuIHNhdHVyYXRlKHZlYzMocmdiMnNyZ2IocmdiLnIgLSBTUkdCX0VQU0lMT04pLCByZ2Iyc3JnYihyZ2IuZyAtIFNSR0JfRVBTSUxPTiksIHJnYjJzcmdiKHJnYi5iIC0gU1JHQl9FUFNJTE9OKSkpOwp9Cgp2ZWM0IHJnYjJzcmdiKHZlYzQgcmdiKSB7CiAgICByZXR1cm4gdmVjNChyZ2Iyc3JnYihyZ2IucmdiKSwgcmdiLmEpOwp9CgojZW5kaWYKCgoKI2lmbmRlZiBGTkNfTUlYT0tMQUIKI2RlZmluZSBGTkNfTUlYT0tMQUIKdmVjMyBtaXhPa2xhYiggdmVjMyBjb2xBLCB2ZWMzIGNvbEIsIGZsb2F0IGggKSB7CgogICAgI2lmZGVmIE1JWE9LTEFCX0NPTE9SU1BBQ0VfU1JHQgogICAgY29sQSA9IHNyZ2IycmdiKGNvbEEpOwogICAgY29sQiA9IHNyZ2IycmdiKGNvbEIpOwogICAgI2VuZGlmCgogICAgCiAgICBjb25zdCBtYXQzIGtDT05FdG9MTVMgPSBtYXQzKCAgICAgICAgICAgICAgICAKICAgICAgICAgMC40MTIxNjU2MTIwLCAgMC4yMTE4NTkxMDcwLCAgMC4wODgzMDk3OTQ3LAogICAgICAgICAwLjUzNjI3NTIwODAsICAwLjY4MDcxODk1ODQsICAwLjI4MTg0NzQxNzQsCiAgICAgICAgIDAuMDUxNDU3NTY1MywgIDAuMTA3NDA2NTc5MCwgIDAuNjMwMjYxMzYxNik7CiAgICBjb25zdCBtYXQzIGtMTVN0b0NPTkUgPSBtYXQzKAogICAgICAgICA0LjA3NjcyNDUyOTMsIC0xLjI2ODE0Mzc3MzEsIC0wLjAwNDExMTk4ODUsCiAgICAgICAgLTMuMzA3MjE2ODgyNywgIDIuNjA5MzMyMzIzMSwgLTAuNzAzNDc2MzA5OCwKICAgICAgICAgMC4yMzA3NTkwNTQ0LCAtMC4zNDExMzQ0MjkwLCAgMS43MDY4NjI1Njg5KTsKICAgICAgICAgICAgICAgICAgICAKICAgIAogICAgdmVjMyBsbXNBID0gcG93KCBrQ09ORXRvTE1TICogY29sQSwgdmVjMygxLjAvMy4wKSApOwogICAgdmVjMyBsbXNCID0gcG93KCBrQ09ORXRvTE1TICogY29sQiwgdmVjMygxLjAvMy4wKSApOwogICAgCiAgICB2ZWMzIGxtcyA9IG1peCggbG1zQSwgbG1zQiwgaCApOwogICAgCiAgICAKICAgIHZlYzMgcmdiID0ga0xNU3RvQ09ORSoobG1zKmxtcypsbXMpOwoKICAgICNpZmRlZiBNSVhPS0xBQl9DT0xPUlNQQUNFX1NSR0IKICAgIHJldHVybiByZ2Iyc3JnYihyZ2IpOwogICAgI2Vsc2UKICAgIHJldHVybiByZ2I7CiAgICAjZW5kaWYKfQoKdmVjNCBtaXhPa2xhYiggdmVjNCBjb2xBLCB2ZWM0IGNvbEIsIGZsb2F0IGggKSB7CiAgICByZXR1cm4gdmVjNCggbWl4T2tsYWIoY29sQS5yZ2IsIGNvbEIucmdiLCBoKSwgbWl4KGNvbEEuYSwgY29sQi5hLCBoKSApOwp9CiNlbmRpZgoKICAgIAoKZmxvYXQgcmF5U3RyZW5ndGgodmVjMiByYXlTb3VyY2UsIHZlYzIgcmF5UmVmRGlyZWN0aW9uLCB2ZWMyIGNvb3JkLCBmbG9hdCBzZWVkQSwgZmxvYXQgc2VlZEIsIGZsb2F0IHNwZWVkKQoKewoKCXZlYzIgc291cmNlVG9Db29yZCA9IGNvb3JkIC0gcmF5U291cmNlOwoKCWZsb2F0IGNvc0FuZ2xlID0gZG90KG5vcm1hbGl6ZShzb3VyY2VUb0Nvb3JkKSwgcmF5UmVmRGlyZWN0aW9uKTsKCgkKCglyZXR1cm4gY2xhbXAoCgoJCSguNDUgKyAwLjE1ICogc2luKGNvc0FuZ2xlICogc2VlZEEgKyB1X3RpbWUgKiBzcGVlZCkpICsKCgkJKDAuMyArIDAuMiAqIGNvcygtY29zQW5nbGUgKiBzZWVkQiArIHVfdGltZSAqIHNwZWVkKSksCgoJCXVfcmVhY2gsIDEuMCkgKgoKCQljbGFtcCgodV9yZXNvbHV0aW9uLnggLSBsZW5ndGgoc291cmNlVG9Db29yZCkpIC8gdV9yZXNvbHV0aW9uLngsIHVfcmVhY2gsIDEuMCk7Cgp9CgoKCnZvaWQgbWFpbigpCgp7CgoJdmVjMiB1diA9IGdsX0ZyYWdDb29yZC54eSAvIHVfcmVzb2x1dGlvbi54eTsKCgl1di55ID0gMS4wIC0gdXYueTsKCgl2ZWMyIGNvb3JkID0gdmVjMihnbF9GcmFnQ29vcmQueCwgdV9yZXNvbHV0aW9uLnkgLSBnbF9GcmFnQ29vcmQueSk7CgoJZmxvYXQgc3BlZWQgPSB1X3JheXMgKiAxMC4wOwoKCQoKCS8vIFNldCB0aGUgcGFyYW1ldGVycyBvZiB0aGUgc3VuIHJheXMKCgl2ZWMyIHJheVBvczEgPSB2ZWMyKHVfcmVzb2x1dGlvbi54ICogMC43LCB1X3Jlc29sdXRpb24ueSAqIC0wLjQpOwoKCXZlYzIgcmF5UmVmRGlyMSA9IG5vcm1hbGl6ZSh2ZWMyKDEuMCwgLTAuMTE2KSk7CgoJZmxvYXQgcmF5U2VlZEExID0gMzYuMjIxNCpzcGVlZDsKCglmbG9hdCByYXlTZWVkQjEgPSAyMS4xMTM0OSpzcGVlZDsKCglmbG9hdCByYXlTcGVlZDEgPSAxLjUqc3BlZWQ7CgoJCgoJdmVjMiByYXlQb3MyID0gdmVjMih1X3Jlc29sdXRpb24ueCAqIDAuOCwgdV9yZXNvbHV0aW9uLnkgKiAtMC42KTsKCgl2ZWMyIHJheVJlZkRpcjIgPSBub3JtYWxpemUodmVjMigxLjAsIDAuMjQxKSk7CgoJZmxvYXQgcmF5U2VlZEEyID0gMjIuMzk5MTAqc3BlZWQ7CgoJZmxvYXQgcmF5U2VlZEIyID0gMTguMDIzNCpzcGVlZDsKCglmbG9hdCByYXlTcGVlZDIgPSAxLjEqc3BlZWQ7CgoJCgoJLy8gQ2FsY3VsYXRlIHRoZSBjb2xvdXIgb2YgdGhlIHN1biByYXlzIG9uIHRoZSBjdXJyZW50IGZyYWdtZW50CgoJdmVjNCByYXlzMSA9CgoJCXZlYzQoMC4sMC4sMC4sIC4wKSArCgoJCXJheVN0cmVuZ3RoKHJheVBvczEsIHJheVJlZkRpcjEsIGNvb3JkLCByYXlTZWVkQTEsIHJheVNlZWRCMSwgcmF5U3BlZWQxKSAqIHVfY29sb3JzWzBdOwoKCSAKCgl2ZWM0IHJheXMyID0KCgkJdmVjNCgwLiwwLiwwLiwgLjApICsKCgkJcmF5U3RyZW5ndGgocmF5UG9zMiwgcmF5UmVmRGlyMiwgY29vcmQsIHJheVNlZWRBMiwgcmF5U2VlZEIyLCByYXlTcGVlZDIpICogdV9jb2xvcnNbMV07CgoJCgoJdmVjNCBmcmFnQ29sb3IgPSAocmF5czEpICsgKHJheXMyKTsKCgkKCgkvLyBBdHRlbnVhdGUgYnJpZ2h0bmVzcyB0b3dhcmRzIHRoZSBib3R0b20sIHNpbXVsYXRpbmcgbGlnaHQtbG9zcyBkdWUgdG8gZGVwdGguCgoJZmxvYXQgYnJpZ2h0bmVzcyA9IDEuMCp1X3JlYWNoIC0gKGNvb3JkLnkgLyB1X3Jlc29sdXRpb24ueSk7CgoJZnJhZ0NvbG9yICo9IChicmlnaHRuZXNzICsgKDAuNSsgdV9pbnRlbnNpdHkpKTsKCgkvL2ZyYWdDb2xvciA9IHBvdyhmcmFnQ29sb3IsdV9icmlnaHRuZXNzKjEwLjApOwoKCWdsX0ZyYWdDb2xvciA9IGZyYWdDb2xvcjsKCn0KCgoKYCwKICAgICAgICAgICAgICAgIHZlcnRleFNoYWRlcjogYC8vIFZlcnRleCBzaGFkZXIKCnZvaWQgbWFpbigpIHsKICBnbF9Qb3NpdGlvbiA9IHByb2plY3Rpb25NYXRyaXggKiBtb2RlbFZpZXdNYXRyaXggKiB2ZWM0KHBvc2l0aW9uLCAxLjApOwp9YCwKICAgICAgICAgICAgICAgIHVuaWZvcm1zOiB7dV9jb2xvcnM6IHt2YWx1ZTogW25ldyBUSFJFRS5WZWN0b3I0KDAuMiwwLjM2NDcwNTg4MjM1Mjk0MTE2LDAuNDAzOTIxNTY4NjI3NDUxLDEpLG5ldyBUSFJFRS5WZWN0b3I0KDAuMjc4NDMxMzcyNTQ5MDE5NiwwLjQ1ODgyMzUyOTQxMTc2NDcsMC41MDE5NjA3ODQzMTM3MjU1LDEpXX0sdV9pbnRlbnNpdHk6IHt2YWx1ZTogMC42NTJ9LHVfcmF5czoge3ZhbHVlOiAwLjA5NH0sdV9yZWFjaDoge3ZhbHVlOiAwLjI1MX0sdV90aW1lOiB7dmFsdWU6IDB9LHVfbW91c2U6IHt2YWx1ZTogWzAsMF19LHVfcmVzb2x1dGlvbjoge3ZhbHVlOiBbMjQwLDU4MF19fSwKICAgICAgICAgICAgICAgIHdpcmVmcmFtZTogZmFsc2UsIAogICAgICAgICAgICAgICAgd2lyZWZyYW1lTGluZXdpZHRoOiAwLAogICAgICAgICAgICAgICAgZGl0aGVyaW5nOiBmYWxzZSwKICAgICAgICAgICAgICAgIGZsYXRTaGFkaW5nOiB0cnVlLAogICAgICAgICAgICAgICAgZG91YmxlU2lkZWQ6IHRydWUsCiAgICAgICAgICAgICAgICBnbHNsVmVyc2lvbjogIjEwMCIKICAgICAgICAgICAgfSkKICAgICAgICApOyAgCiAgICB9CgogICAgUmVhY3RET00ucmVuZGVyKFJlYWN0LmNyZWF0ZUVsZW1lbnQoQ2FudmFzLHsKICAgICAgICAgICAgZ2w6IHsKICAgICAgICAgICAgICAgIHByZXNlcnZlRHJhd2luZ0J1ZmZlcjogdHJ1ZSwKICAgICAgICAgICAgICAgIHByZW11bHRpcGxpZWRBbHBoYTogZmFsc2UsCiAgICAgICAgICAgICAgICBhbHBoYTogdHJ1ZSwKICAgICAgICAgICAgICAgIHRyYW5zcGFyZW50OiB0cnVlLAogICAgICAgICAgICAgICAgYW50aWFsaWFzOiB0cnVlLAogICAgICAgICAgICAgICAgcHJlY2lzaW9uOiAiaGlnaHAiLAogICAgICAgICAgICAgICAgcG93ZXJQcmVmZXJlbmNlOiAiaGlnaC1wZXJmb3JtYW5jZSIKICAgICAgICAgICAgfSwKICAgICAgICAgICAgcmVzaXplOnsKICAgICAgICAgICAgICAgIGRlYm91bmNlOiAwLAogICAgICAgICAgICAgICAgc2Nyb2xsOiBmYWxzZSwKICAgICAgICAgICAgICAgIG9mZnNldFNpemU6IHRydWUKICAgICAgICAgICAgfSwKICAgICAgICAgICAgZHByOiAxLAogICAgICAgICAgICBjYW1lcmE6IHsKICAgICAgICAgICAgICAgIGZvdjogNzUsCiAgICAgICAgICAgICAgICBuZWFyOiAwLjEsCiAgICAgICAgICAgICAgICBmYXI6IDEwMDAsCiAgICAgICAgICAgICAgICBwb3NpdGlvbjogWzAsMCw1XQogICAgICAgICAgICB9LAogICAgICAgICAgICBzdHlsZTp7IGhlaWdodDogIjEwMCUiLCB3aWR0aDogIjEwMCUiIH0KICAgICAgICB9LAogICAgICAgIFJlYWN0LmNyZWF0ZUVsZW1lbnQoVGV4dHVyZU1lc2gpICAgICAgICAgICAgICAgICAgICAgICAgICAgCiAgICApLCBlbWJlZFJvb3QpOwo8L3NjcmlwdD4KICAgICAgICA8L2hlYWQ+CiAgICAgICAgPGJvZHk+CjwhLS0gQVNTRVQgTk9UIElOTElORUQ6IGFzc2V0cy9ib29rLjVmYjExYjhkLnN2ZyAtLT4KCjwhLS0gQVNTRVQgTk9UIElOTElORUQ6IGFzc2V0cy9seWdpYS5mNzQ5MDU5NC5zdmcgLS0+Cgo8IS0tIEFTU0VUIE5PVCBJTkxJTkVEOiBhc3NldHMvb3ZlcmZsb3cuOGQ1MDQxNWQuc3ZnIC0tPgoKPCEtLSBBU1NFVCBOT1QgSU5MSU5FRDogYXNzZXRzL3RyYXNoLjUyNGRiY2QzLnN2ZyAtLT4KCjwhLS0gQVNTRVQgTk9UIElOTElORUQ6IGFzc2V0cy92ZXJ0aWNhbC45MDYxMDg0OS5zdmcgLS0+Cgo8IS0tIEFTU0VUIE5PVCBJTkxJTkVEOiBhc3NldHMvaG9yaXpvbnRhbC40NGY3NzFmOC5zdmcgLS0+Cgo8IS0tIEFTU0VUIE5PVCBJTkxJTkVEOiBhc3NldHMvYWRkLmE1NTI0MGRkLnN2ZyAtLT4KCjwhLS0gQVNTRVQgTk9UIElOTElORUQ6IGFzc2V0cy9zaWcuODUwYTg1ZTcuc3ZnIC0tPgoKPCEtLSBBU1NFVCBOT1QgSU5MSU5FRDogYXNzZXRzL21pbnVzLjFlMWEwYWJkLnN2ZyAtLT4KCjwhLS0gQVNTRVQgTk9UIElOTElORUQ6IGFzc2V0cy9lZmZlY3QuNDU1NzI0M2Yuc3ZnIC0tPgoKPCEtLSBBU1NFVCBOT1QgSU5MSU5FRDogYXNzZXRzL3Zpc2libGUuYzZkNGUxYzAuc3ZnIC0tPgoKPCEtLSBBU1NFVCBOT1QgSU5MSU5FRDogYXNzZXRzL2hpZGRlbi41YTRmYzI1NC5zdmcgLS0+Cgo8IS0tIEFTU0VUIE5PVCBJTkxJTkVEOiBhc3NldHMvd29ya2VyLmI3NjFmYjExLmpzIC0tPgo8L2JvZHk+CiAgICAgICAgPC9odG1sPg=='
}

// Directives
export const useVClickOutside = () => ({
  vClickOutside: {
    mounted: function (el, binding) {
      el.clickOutsideEvent = function (event) {
        if (el == event.target || el.contains(event.target)) {
          return
        }

        if (binding.value) {
          binding.value(event, el)
        }
      }
      document.addEventListener('click', el.clickOutsideEvent)
    },
    updated: function (el, binding) {
      document.removeEventListener('click', el.clickOutsideEvent)
      el.clickOutsideEvent = null
      el.clickOutsideEvent = function (event) {
        if (el == event.target || el.contains(event.target)) {
          return
        }

        if (binding.value) {
          binding.value(event, el)
        }
      }
      document.addEventListener('click', el.clickOutsideEvent)
    },
    unmounted: function (el) {
      document.removeEventListener('click', el.clickOutsideEvent)
    },
  },
})

export const useVObserveVisibility = () => ({
  vObserveVisibility: {
    beforeMount: (el, binding, vnode) => {
      vnode.context = binding.instance
      ObserveVisibility.bind(el, binding, vnode)
    },
    update: ObserveVisibility.update,
    unmounted: ObserveVisibility.unbind,
  },
})
